import { defineStore } from "pinia";
import type { ToastMessageOptions } from "primevue/toast";
import type { IMenuItem } from "~/scad-shared/types/interfaces";
import type { Lookup, LookupCategory } from "~/types/lookup";

async function getLookups(): Promise<Lookup[]> {
  const res = await $fetch("/scad/lookups", {
    query: {
      IsEnabled: true,
    },
  });

  return res?.Results ?? [];
}
async function getLookupCategories(): Promise<LookupCategory[]> {
  const res = await $fetch("/scad/lookupCategories", {
    query: {
      IsEnabled: true,
    },
  });

  return res?.Results ?? [];
}

export const useAppStore = defineStore("app", () => {
  // const auth = useAuth();
  // Setup
  const {
    data: metaMenuData,
    pending: metaMenuPending,
    refresh: metaMenuRefresh,
  } = useFetch(`/scad/metamenus?OrderBy=SortOrder`, {
    immediate: false,
    transform(res): unknown[] {
      return res.Results  ?? [];
    }
  });

  const { data: lookup, pending: lookupsPending } = useAsyncData(async () => {
    const responseData: {
      values: Lookup[];
      categories: LookupCategory[];
      categoryMap: { [key: number]: LookupCategory };
      categoryNameMap: { [key: string]: LookupCategory };
      valueMap: { [key: number]: Lookup };
      categoryValues: { [key: number]: Lookup[] };
    } = {
      values: [],
      categories: [],
      categoryMap: {},
      categoryNameMap: {},
      valueMap: {},
      categoryValues: {},
    };

    const [lookups, categories] = await Promise.all([
      getLookups(),
      getLookupCategories(),
    ]);

    categories.forEach((category) => {
      responseData.categoryMap[category.Id] = category;
      responseData.categoryNameMap[category.Description] = category;
    });
    categories.forEach((category) => {
      responseData.categoryValues[category.Id] = lookups.filter((lookup) => {
        return lookup.LookupCategoryId == category.Id;
      });
    });
    lookups.forEach((lookup) => {
      responseData.valueMap[lookup.Id] = lookup;
    });

    return responseData;
  });

  // States
  const loadingStack = ref<{ [key: string]: boolean }>({});
  const blockLoading = ref<boolean>(false);
  const thirdLevelItems = ref<IMenuItem[]>([]);
  // State for filter forms
  const tableFilterForms = ref<{ [key: string]: unknown }>({});
  // State for page size
  const pageSize = ref<{ [key: string]: unknown }>({});
  // State for current page
  const currentPage = ref<{ [key: string]: unknown }>({});

  const clearPersistentData = () => {
    tableFilterForms.value = {};
    pageSize.value = {};
    currentPage.value = {};
  };

  // Getters
  const loading = computed<boolean>(() => {
    return Object.keys(loadingStack.value).length !== 0;
  });

  const getCategoryLookups = computed<(categoryName: string) => Lookup[]>(
    () => {
      return (categoryName: string) => {
        if (lookup.value && categoryName in lookup.value?.categoryNameMap) {
          return (
            lookup.value.categoryValues[
              lookup.value.categoryNameMap[categoryName].Id
            ] ?? []
          );
        } else {
          throw new Error(`Lookup category ${categoryName} not found.`);
        }
      };
    }
  );

  const menuItemList = computed<IMenuItem[]>(() => {
    const itemList: IMenuItem[] = [];
    metaMenuData.value?.forEach((res) => {
      let link = "";
      if (res.Link) {
        link = res.Link.replace("/table/", "");
      }

      const menuItem: IMenuItem = {
        id: String(res.Id),
        label: res.Name,
        to: res.Link,
        items: [],
        icon: res.Icon,
        parent: res.MenuGroup,
        table: link,
        order: res.SortOrder,
        groupOrder: res.MetaGroupSortOrder,
      };

      itemList.push(menuItem);
    });

    return itemList;
  });

  const toast = useToast();
  function pushToast(options: ToastMessageOptions) {
    toast.add(options);
  }

  function menuItemIcon(path: string): string | undefined {
    const matchingMenuItems = menuItemList.value.filter((menuItem) => {
      return (
        menuItem.to !== "/" &&
        (menuItem.to?.startsWith(path) ||
          (menuItem.to && path.startsWith(menuItem.to)))
      );
    });

    if (matchingMenuItems.length > 0) {
      return matchingMenuItems[0].icon;
    }
  }
  const searchMenuItemList = computed<(search: string) => IMenuItem[]>(() => {
    return (search: string) => {
      if (menuItemListGrouped.value.length === 0) {
        return [];
      }

      const results: IMenuItem[] = [];
      const searchLower = search.toLowerCase();

      // Search through top level and second level items
      menuItemListGrouped.value.forEach((topItem) => {
        // Check second level items
        topItem.items.forEach((subItem) => {
          if (subItem.label.toLowerCase().includes(searchLower)) {
            if (!results.includes(subItem)) {
              results.push(subItem);
            }
          }
        });
      });
      if (results.length > 0) {
        return results;
      } else {
        return [{ label: "No search results found", id: "0", items: [] }];
      }
    };
  });

  const menuItemListGrouped = computed<IMenuItem[]>(() => {
    const groupedItemList: IMenuItem[] = [];
    const parentMap: { [parent: string]: IMenuItem } = {};

    menuItemList.value.forEach((menuItem) => {
      parentMap[menuItem.label] = menuItem;
    });

    menuItemList.value.forEach((menuItem) => {
      if (!menuItem.parent) {
        groupedItemList.push(menuItem);
      } else {
        const parentItem = parentMap[menuItem.parent];
        if (parentItem) {
          if (!parentItem.parent) {
            parentItem.items.push(menuItem);
          }
        }
      }
    });

    menuItemList.value.forEach((menuItem) => {
      if (menuItem.parent) {
        const parentItem = parentMap[menuItem.parent];
        if (parentItem && parentItem.parent) {
          if (parentItem.label === menuItem.parent) {
            if (
              !thirdLevelItems.value.find((e) => e.label === menuItem.label)
            ) {
              menuItem.table = parentItem.table;
              thirdLevelItems.value.push(menuItem);
            }
          }
        }
      }
    });

    // Sort items by groupOrder within their parent groups
    for (let parentKey in parentMap) {
      parentMap[parentKey].items.sort((a, b) => {
        if (!a.groupOrder || !b.groupOrder) {
          return 0;
        }
        return a.groupOrder - b.groupOrder;
      });
    }

    // Sort top-level items by order
    return groupedItemList.sort((a, b) => {
      if (!a.order || !b.order) {
        return 0;
      }
      return a.order - b.order;
    });
  });

  return {
    loading,
    loadingStack,
    metaMenuData,
    menuItemList,
    metaMenuPending,
    menuItemListGrouped,
    searchMenuItemList,
    blockLoading,
    menuItemIcon,
    metaMenuRefresh,
    thirdLevelItems,
    lookup,
    getCategoryLookups,
    pushToast,
    tableFilterForms,
    pageSize,
    currentPage,
    clearPersistentData
  };
}, {
  persist: {
    pick: ["tableFilterForms", "pageSize", "currentPage"],
  }
});

